/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { AsyncValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { DisplayPriceAndCurrency, LocalizedData } from '../../../common';
import { Content, ContentExtras, Page, PageItem } from '../../../domain';
import { AbstractBookmarkService } from '../../services';
import { AbstractNavigationService } from '../../services/abstract-navigation.service';
import { MarketingMapperService } from '../../services/marketing-mapper.service';
import { mapLocalizedQuestionsToQuestions, throwNever } from '../../utils';
import { BasicCarouselViewModel } from '../basic-carousel/basic-carousel.view-model';
import { CollectionCarouselViewModel } from '../collection-carousel/collection-carousel.view-model';
import { ContinueWatchingCarouselViewModel } from '../continue-watching-carousel/continue-watching-carousel.view-model';
import { MarketingDeviceModuleViewModel } from '../device-module/marketing-device-module-view.model';
import { FaqViewModel } from '../faq/faq-view-model';
import { GenericCarouselViewModel } from '../generic-carousel/generic-carousel.view-model';
import { HeroItemActionClickType, HeroItemViewModel } from '../hero-item/hero-item.view-model';
import { HeroViewModel } from '../hero/hero.view-model';
import { MarketingCtaViewModel } from '../marketing-cta/marketing-cta.view-model';
import { MarketingEmailBlockerViewModel } from '../marketing-email-blocker/marketing-email-blocker.view-model';
import { MarketingEmailCollectorViewModel } from '../marketing-email-collector/marketing-email-collector.view-model';
import { MarketingHeroViewModel } from '../marketing-hero/marketing-hero-view-model';
import { MarketingKidsViewModel } from '../marketing-kids/marketing-kids-view-model';
import { MarketingMultipleContentTitleBannerViewModel } from '../marketing-multiple-content-title-banner/marketing-multiple-content-title-banner.view-model';
import { MarketingStudioViewModel } from '../marketing-studio/marketing-studio.view-model';
import { MarketingTrendingViewModel } from '../marketing-trending/marketing-trending.view-model';
import { MarketingUspViewModel } from '../marketing-usp/marketing-usp.view-model';
import { MyListCarouselViewModel } from '../my-list-carousel/my-list-carousel.view-model';
import { RecommendationCarouselViewModel } from '../recommendation-carousel/recommendation-carousel.view-model';
import { SeasonBasicCarouselViewModel } from '../season-basic-carousel/season-basic-carousel.view-model';
import { SeasonCollectionCarouselViewModel } from '../season-collection-carousel/season-collection-carousel.view-model';
import { SpotlightViewModel } from '../spotlight-tray/spotlight-tray.view-model';
import { TopCarouselViewModel } from '../top-carousel/top-carousel.view-model';
import { PageScreenMapperService } from './page-screen-mapper.service';
import { PageScreenViewModel } from './page-screen.view-model';

export abstract class AbstractPageScreenService {
  public abstract readonly currentExtras$: Observable<ContentExtras>;
  public abstract readonly currentPage$: Observable<Page>;
  public abstract readonly currentResolved$: Observable<Content[][]>;
  public abstract readonly shouldShowSeoTitle$: Observable<boolean>;
  public abstract readonly asyncPrimaryCredentialValidator: AsyncValidatorFn;
  private currentPageViewModelInstance$: Observable<PageScreenViewModel>;
  get vm$(): Observable<PageScreenViewModel> {
    // Singleton pattern is used, as the inheritor may use it as singleton for more pages.
    if (!this.currentPageViewModelInstance$) {
      this.currentPageViewModelInstance$ = this.getCurrentPageViewModel$();
    }

    return this.currentPageViewModelInstance$;
  }

  constructor(
    protected readonly translateService: TranslateService,
    private readonly marketingMapperService: MarketingMapperService,
    protected readonly pageScreenMapperService: PageScreenMapperService,
    protected readonly bookmarkService: AbstractBookmarkService
  ) {}

  private getCurrentPageViewModel$(): Observable<PageScreenViewModel> {
    return combineLatest([
      this.currentResolved$,
      this.currentPage$,
      this.currentExtras$,
      this.shouldShowSeoTitle$,
      this.translateService.onLangChange.pipe(startWith('')),
      this.bookmarkService.bookmarksChanged$,
    ]).pipe(
      filter(([resolved, page]) => !!resolved && !!page),
      map(([resolved, page, extras, shouldShowSeoTitle]) => {
        return {
          title: page.title,
          pageItems: page.pageItems
            .map(this.pageItemMapper(resolved, this.translateService.currentLang, page.slug, extras))
            .map((item, index) => ({ ...item, resolved: resolved[index] !== undefined, readonlyMode: false })),
          shouldShowSeoTitle,
        } as PageScreenViewModel;
      })
    );
  }

  private pageItemMapper(
    contentGroupData: Content[][],
    currentLang: string,
    pageSlug: string,
    extras: ContentExtras
  ): (
    value: PageItem,
    index: number,
    array: PageItem[]
  ) =>
    | BasicCarouselViewModel
    | TopCarouselViewModel
    | CollectionCarouselViewModel
    | SeasonBasicCarouselViewModel
    | SeasonCollectionCarouselViewModel
    | GenericCarouselViewModel
    | HeroViewModel
    | ContinueWatchingCarouselViewModel
    | MyListCarouselViewModel
    | MarketingMultipleContentTitleBannerViewModel
    | MarketingCtaViewModel
    | MarketingEmailCollectorViewModel
    | MarketingEmailBlockerViewModel
    | RecommendationCarouselViewModel
    | MarketingKidsViewModel
    | MarketingUspViewModel
    | MarketingStudioViewModel
    | MarketingTrendingViewModel
    | MarketingHeroViewModel
    | FaqViewModel
    | MarketingDeviceModuleViewModel
    | SpotlightViewModel {
    // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
    return (
      pageItem: PageItem,
      index: number,
      array: PageItem[]
    ):
      | BasicCarouselViewModel
      | TopCarouselViewModel
      | CollectionCarouselViewModel
      | SeasonBasicCarouselViewModel
      | SeasonCollectionCarouselViewModel
      | GenericCarouselViewModel
      | HeroViewModel
      | ContinueWatchingCarouselViewModel
      | MyListCarouselViewModel
      | MarketingMultipleContentTitleBannerViewModel
      | MarketingCtaViewModel
      | MarketingEmailCollectorViewModel
      | MarketingEmailBlockerViewModel
      | RecommendationCarouselViewModel
      | MarketingKidsViewModel
      | MarketingUspViewModel
      | MarketingStudioViewModel
      | MarketingTrendingViewModel
      | MarketingHeroViewModel
      | FaqViewModel
      | MarketingDeviceModuleViewModel
      | SpotlightViewModel => {
      switch (pageItem.type) {
        case 'Carousel':
          return this.pageScreenMapperService.carouselTypeMapper(
            pageItem,
            contentGroupData,
            index,
            currentLang,
            this.getNavigationService().getUrl('listView', pageSlug, pageItem.slug)
          );
        case 'SeasonCarousel':
          return this.pageScreenMapperService.seasonCarouselTypeMapper(
            pageItem,
            contentGroupData,
            index,
            currentLang,
            this.getNavigationService().getUrl('listView', pageSlug, pageItem.slug)
          );
        case 'GenericCarousel':
          return this.pageScreenMapperService.genericCarouselMapper(
            pageItem,
            currentLang,
            this.getNavigationService().getUrl('listView', pageSlug, pageItem.slug),
            pageSlug
          );
        case 'RecommendationCarousel':
          return this.pageScreenMapperService.recommendationCarouselMapper(
            pageItem,
            contentGroupData,
            index,
            currentLang,
            this.getNavigationService().getUrl('listView', pageSlug, pageItem.slug),
            extras[pageItem.slug] as LocalizedData<string>
          );
        case 'Hero':
          const heroTitle = extras[pageItem.slug];
          return {
            type: 'Hero',
            slug: pageItem.slug,
            items: this.pageScreenMapperService.heroContentPreviewMapper(pageItem.heroItems, contentGroupData[index], currentLang),
            title: heroTitle ? heroTitle[currentLang] : undefined,
          } as HeroViewModel;
        case 'MarketingCta':
          return this.marketingMapperService.mapMarketingCtaToVM(pageItem, 0);
        case 'MarketingEmailCollector':
          return this.marketingMapperService.mapMarketingEmailCollectorToVM(pageItem, extras[pageItem.slug] as DisplayPriceAndCurrency);
        case 'MarketingMultipleContentTitleBanner':
          return this.marketingMapperService.mapMarketingMultipleContentTitleBannerToVM(pageItem, 0);
        case 'MarketingStudio':
          return this.marketingMapperService.mapMarketingStudioToVM(pageItem, 0);
        case 'MarketingTrending':
          return this.marketingMapperService.mapMarketingTrendingToVM(pageItem, 0);
        case 'Faq':
          return {
            type: pageItem.type,
            title: pageItem.titleLocalized[currentLang],
            slug: pageItem.slug,
            questions: mapLocalizedQuestionsToQuestions(pageItem.questions, currentLang),
          };
        case 'BuiltIn': {
          // eslint-disable-next-line sonarjs/no-nested-switch
          switch (pageItem.builtInType) {
            case 'ContinueWatchingCarousel':
              return this.pageScreenMapperService.continueWatchingCarouselTypeMapper(
                pageItem,
                contentGroupData,
                index,
                currentLang,
                this.getNavigationService().getUrl('listView', pageSlug, pageItem.slug)
              );
            case 'WatchlistCarousel':
              return this.pageScreenMapperService.myListCarouselTypeMapper(
                pageItem,
                contentGroupData,
                index,
                currentLang,
                this.getNavigationService().getUrl('myList')
              );
            default:
              return undefined;
          }
        }
        case 'MarketingKids': {
          return this.marketingMapperService.mapMarketingKidsToVM(pageItem, 0);
        }
        case 'MarketingUsp': {
          return this.marketingMapperService.mapMarketingUspToVM(pageItem, extras[pageItem.slug] as DisplayPriceAndCurrency);
        }
        case 'MarketingHero': {
          return this.marketingMapperService.mapMarketingHeroToVM(pageItem, extras[pageItem.slug] as DisplayPriceAndCurrency);
        }
        case 'MarketingDeviceModule': {
          return this.marketingMapperService.mapMarketingDeviceModule(pageItem);
        }
        case 'MarketingEmailBlocker':
          return this.pageScreenMapperService.marketingEmailBlockerMapper(
            pageItem,
            extras[pageItem.slug] as DisplayPriceAndCurrency,
            currentLang
          );
        case 'SpotlightTray': {
          return this.pageScreenMapperService.spotlightMapper(pageItem, currentLang);
        }
        default:
          throwNever(pageItem);
      }
    };
  }

  public abstract getNavigationService(): AbstractNavigationService;
  public abstract loadPage(): void;
  public abstract unloadPage(): void;
  public abstract resolvePageItems(index: number): void;
  public abstract measureProductClick(carouselItemGuid: string, carouselTitle: string, carouselItemType: string): void;
  public abstract measureProductImpression(carouselItemGuid: string, index: number, carouselTitle: string, carouselItemType: string): void;
  public abstract measurePromotionClick(heroItem: HeroItemViewModel, index: number): void;
  public abstract measurePromotionImpression(heroItems: HeroItemViewModel[]): void;
  public abstract measureHeroItemActionClick(actionInfo: {
    actionType: HeroItemActionClickType;
    videoLength: number;
    seriesTitle: string;
    heroItem: HeroItemViewModel;
    buttonText: string;
  }): void;
  public abstract measureHeroItemNavigationByTheUser(heroItem: HeroItemViewModel, videoLength: number): void;
  public abstract measureHeroGenreClick(heroItem: HeroItemViewModel, genreName: string, videoLength: number): void;
  public abstract measureCarouselTitleExploreAllClick(carouselTitle: string, isGenrePage: boolean): void;
  public abstract measureStartFreeTrialClick(eventLabel: string, buttonName: string): void;
  public abstract measureSpotlightButtonClick(buttonText: string): void;
  public abstract removeHrefLinkTags(): void;
  public abstract removeCanonicalTag(): void;
  public abstract signIn(): void;
  public abstract signUp(email?: string): void;
  public abstract addNewPassword(): void;
}
